import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

const cartItemKey = (item) => `${item.id}-${item.size || 'U'}`;

const parsePrice = (priceStr) => {
  if (!priceStr) return 0;
  const normalized = String(priceStr).replace(/R\$\s?/, '').replace('$', '').replace(',', '.');
  return parseFloat(normalized) || 0;
};

const formatPrice = (value) =>
  `R$${value.toFixed(2).replace('.', ',')}`;

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    const savedCart = localStorage.getItem('haka_cart');
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        setCartItems(Array.isArray(parsed) ? parsed : []);
      } catch (err) {
        console.error('Erro ao carregar carrinho do localStorage:', err);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('haka_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product) => {
    const size = product.size || null;
    setCartItems((prevItems) => {
      const existing = prevItems.find(
        (item) => item.id === product.id && (item.size || null) === size
      );
      if (existing) {
        return prevItems.map((item) =>
          item.id === product.id && (item.size || null) === size
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      const key = cartItemKey({ ...product, size });
      return [...prevItems, { ...product, quantity: 1, size, cartKey: key }];
    });
  };

  const removeFromCart = (cartKey) => {
    setCartItems((prev) => prev.filter((item) => (item.cartKey || cartItemKey(item)) !== cartKey));
  };

  const updateQuantity = (cartKey, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartKey);
      return;
    }
    setCartItems((prev) =>
      prev.map((item) =>
        (item.cartKey || cartItemKey(item)) === cartKey ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => setCartItems([]);

  const getTotalPrice = () =>
    cartItems.reduce((total, item) => total + parsePrice(item.price) * item.quantity, 0);

  const getTotalItems = () =>
    cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalPrice,
    getTotalItems,
    cartItemKey,
    formatPrice,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
